import Tweenr from 'tweenr';
import { debounce } from 'lodash';
import GestureHelper, { GestureHelperData } from './GestureHelper';
import ContextMenuBlocker from './ContextMenuBlocker';
import { clamp, toFixedNumber } from './math';
import EventHandlerList from '../../utils/EventHandlerList';
import { EventHandler } from '../../utils/dom-events';
import type ViewportZoomControl from './ViewportZoomControl';
import ViewportElementsBuilder from './ViewportElementsBuilder';
import { PartialClientRect } from './SeatClientRectProvider';

interface Point {
  x: number;
  y: number;
}

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Tween {
  cancel: () => void;
}

const Key = {
  LEFT: 37,
  UP: 38,
  RIGHT: 39,
  DOWN: 40,
  ZERO: 48,
  ZERO_NUMPAD: 96,
  PLUS: 187,
  MINUS: 189,
  ADD: 107,
  SUBTRACT: 109,
  FIREFOX_PLUS: 61,
  FIREFOX_MINUS: 173,
};

const MARGIN = 25;
const defaultMinimumScale = 0.1;
const absoluteMinimumScale = 0.01;
const zoomMultiplier = 1.7;
const zoomMinMaxThreshold = 0.05;

const roundDecimal = (value: number) => toFixedNumber(value, 4);

// iOS runs out of memory and crashes with large 3D transforms:
const use3DTransform = /iPhone|iPad|iPod/.test(window.navigator.userAgent) === false;

const makeZoomControlPerc = (scale: number, minScale: number) => (scale - minScale) / (1 - minScale);

const noop = () => { };

class Viewport {
  el: HTMLElement;

  #scale = 1;

  minScale = 1;

  previousScale = 1;

  transformOrigin: Point = { x: 0, y: 0 };

  contextMenuBlocker: ContextMenuBlocker;

  gestures: GestureHelper;

  containerEl: HTMLElement;

  originMarkerEl: HTMLElement | null;

  tweenr: Tweenr;

  tween: Tween | undefined;

  notifyUpdate: () => void;

  contentRect: Rect = {
    x: 0, y: 0, width: 0, height: 0,
  };

  containerRect = {
    x: 0, y: 0, width: 0, height: 0, half: { width: 0, height: 0 },
  };

  contentEl: HTMLElement | null = null;

  resizeHandlers: EventHandlerList;

  focusHandlers: EventHandlerList;

  keyDownHandler: EventHandler;

  activeDescendent: Element | null = null;

  constructor($root: HTMLElement, onUpdate: () => void, private zoomControl: ViewportZoomControl) {
    const elements = ViewportElementsBuilder.build({
      marginSize: MARGIN,
      drawDebuggingElements: false,
    });

    this.el = elements.el;
    this.containerEl = elements.containerEl;
    this.originMarkerEl = elements.originMarkerEl;

    // Firefox long press via touch triggers context menu
    this.contextMenuBlocker = new ContextMenuBlocker(this.el);

    this.tweenr = new Tweenr();

    this.notifyUpdate = debounce(onUpdate, 20, { leading: true });

    this.gestures = new GestureHelper(this.containerEl, {
      onPanMove: this.onPanMove,
      onPinch: this.onPinch,
      onDoubleClick: this.onDoubleClick,
      onRelease: this.onRelease,
      passive: false,
    });

    this.zoomControl.setCallbacks({
      onZoomIn: this.zoomIn,
      onZoomOut: this.zoomOut,
      onReset: this.fitContents,
    });

    this.resizeHandlers = new EventHandlerList([
      new EventHandler(window, 'resize', debounce(this.onWindowResize, 20), { passive: true }),
      new EventHandler(window, 'scroll', debounce(this.updateContainerRect, 20), { passive: true }),
    ]).start();

    this.focusHandlers = new EventHandlerList([
      new EventHandler(this.el, 'focusin', this.onFocusIn, { capture: true }),
      new EventHandler(this.el, 'focusout', this.onFocusOut),
    ]).start();

    this.keyDownHandler = new EventHandler($root, 'keydown', this.onKeyDown).start();
  }

  get scale() {
    return this.#scale;
  }

  set scale(value: number) {
    this.#scale = roundDecimal(value);
  }

  onFocusIn = (event: FocusEvent) => {
    if (event.target instanceof Element) {
      this.activeDescendent = event.target;
    }

    this.containerEl.scrollTop = 0;
    this.containerEl.scrollLeft = 0;
  };

  onFocusOut = () => {
    this.activeDescendent = null;
  };

  bringRectIntoView = (clientRect: PartialClientRect, margin = 0) => {
    const { containerRect } = this;
    const clientRectLocal = {
      x: clientRect.left - containerRect.x,
      y: clientRect.top - containerRect.y,
      width: clientRect.width,
      height: clientRect.height,
    };

    let offsetX = 0;
    let offsetY = 0;

    if (clientRectLocal.x < margin) {
      offsetX = clientRectLocal.x - margin;
    } else if (clientRectLocal.x + clientRectLocal.width > containerRect.width - margin) {
      offsetX = (clientRectLocal.x + clientRectLocal.width) - (containerRect.width - margin);
    }

    if (clientRectLocal.y < margin) {
      offsetY = clientRectLocal.y - margin;
    } else if (clientRectLocal.y + clientRectLocal.height > containerRect.height - margin) {
      offsetY = (clientRectLocal.y + clientRectLocal.height) - (containerRect.height - margin);
    }

    if (offsetX || offsetY) {
      this.contentRect.x -= Math.round(offsetX);
      this.contentRect.y -= Math.round(offsetY);

      this.render(false);
    }
  };

  startListening() {
    this.gestures.start();
    this.zoomControl.startListening();
  }

  stopListening() {
    this.gestures.stop();
    this.zoomControl.stopListening();
  }

  setContentEl(el: HTMLElement, width = 0, height = 0) {
    this.contentRect = {
      x: 0,
      y: 0,
      width,
      height,
    };

    this.contentEl = el;
    this.containerEl.appendChild(this.contentEl);

    this.updateContainerRect();

    this.fitContents(false);
  }

  onKeyDown = (event: KeyboardEvent) => {
    const key = event.keyCode || event.charCode;

    if (key === Key.ZERO || key === Key.ZERO_NUMPAD) {
      this.fitContents();

      return;
    }

    if (event.shiftKey) {
      const { half } = this.containerRect;
      const panAmount = Math.min(half.width, half.height);
      let panX;
      let panY;

      switch (key) {
        case Key.UP:
          panY = panAmount;
          break;
        case Key.DOWN:
          panY = -panAmount;
          break;
        case Key.LEFT:
          panX = panAmount;
          break;
        case Key.RIGHT:
          panX = -panAmount;
          break;
        default:
          break;
      }

      if (panX || panY) {
        event.preventDefault();

        this.pan(panX, panY);

        return;
      }
    }

    if (!event.ctrlKey && [Key.PLUS, Key.MINUS, Key.FIREFOX_PLUS, Key.FIREFOX_MINUS, Key.ADD, Key.SUBTRACT].includes(key)) {
      event.preventDefault();

      let center;

      if (this.activeDescendent) {
        const rect = this.activeDescendent.getBoundingClientRect();

        center = {
          x: (rect.left + (rect.width * 0.5)) - this.containerRect.x,
          y: (rect.top + (rect.height * 0.5)) - this.containerRect.y,
        };
      }

      if (key === Key.PLUS || key === Key.FIREFOX_PLUS || key === Key.ADD) {
        this.zoomIn(center);
      } else {
        this.zoomOut(center);
      }
    }
  };

  pan(x = 0, y = 0) {
    const { contentRect } = this;

    const tweeningData = {
      x: contentRect.x,
      y: contentRect.y,
    };

    const target = {
      x: contentRect.x + x,
      y: contentRect.y + y,
      duration: 0.3,
      ease: 'quadInOut',
    };

    const restrainedCoords = this.getRestrainedCoordinates(target);

    target.x = restrainedCoords.x;
    target.y = restrainedCoords.y;

    this.startTween(tweeningData, target, () => {
      this.contentRect.x = tweeningData.x;
      this.contentRect.y = tweeningData.y;

      this.render();
    });
  }

  getContentClientRect() {
    return this.contentEl?.getBoundingClientRect();
  }

  calcInitialScale() {
    const { contentRect, containerRect } = this;

    const doubleMargin = MARGIN * 2;

    const fittingWidthPerc = (containerRect.width - doubleMargin) / contentRect.width;
    const fittingHeightPerc = (containerRect.height - doubleMargin) / contentRect.height;
    const fittingScale = Math.min(fittingWidthPerc, fittingHeightPerc);

    const minScale = Math.min(fittingScale, defaultMinimumScale);

    return {
      scale: roundDecimal(Math.max(fittingScale, absoluteMinimumScale)),
      minScale: roundDecimal(Math.max(minScale, absoluteMinimumScale)),
    };
  }

  updateContainerRect = () => {
    const rect = this.containerEl.getBoundingClientRect();

    this.containerRect = {
      x: rect.left,
      y: rect.top,
      width: rect.width,
      height: rect.height,
      half: {
        width: rect.width * 0.5,
        height: rect.height * 0.5,
      },
    };
  };

  onWindowResize = () => {
    this.updateContainerRect();
    this.fitContents(false);
  };

  centerContents() {
    const { containerRect } = this;
    const scaledDimensions = this.getScaledDimensions(this.scale);

    this.contentRect.x = Math.round((containerRect.width - scaledDimensions.width) * 0.5);
    this.contentRect.y = Math.round((containerRect.height - scaledDimensions.height) * 0.5);
  }

  onPanMove = (event: Event, coords: GestureHelperData) => {
    event.preventDefault();

    this.contentRect.x += coords.offset.x;
    this.contentRect.y += coords.offset.y;

    this.render();
  };

  onRelease = (event: Event, coords: GestureHelperData) => {
    event.preventDefault();

    if (coords.didPinch && coords.didPan) {
      const minMaxedScale = clamp(this.scale, this.minScale, 1);
      if (this.scale !== minMaxedScale) {
        this.animateToScale(minMaxedScale, this.transformOrigin, 0.3, 'backOut');
      }
    }
  };

  getRestrainedCoordinates(contentRect: Point) {
    const { containerRect } = this;
    const bounds = this.getScaledDimensions(this.scale);

    const xMax = Math.ceil(containerRect.half.width);
    const xMin = Math.floor(containerRect.half.width - bounds.width);
    const yMax = Math.ceil(containerRect.half.height);
    const yMin = Math.floor(containerRect.half.height - bounds.height);

    const tooFarUp = contentRect.y < yMin;
    const tooFarDown = contentRect.y > yMax;
    const tooFarLeft = contentRect.x < xMin;
    const tooFarRight = contentRect.x > xMax;

    const coords = { x: contentRect.x, y: contentRect.y };

    // console.log({ tooFarUp, tooFarDown, tooFarLeft, tooFarRight });

    if (tooFarRight) {
      coords.x = xMax;
    } else if (tooFarLeft) {
      coords.x = xMin;
    }

    if (tooFarDown) {
      coords.y = yMax;
    } else if (tooFarUp) {
      coords.y = yMin;
    }

    return coords;
  }

  onPinch = (event: Event, coords: GestureHelperData) => {
    event.preventDefault();

    this.contentRect.x += coords.offset.x;
    this.contentRect.y += coords.offset.y;

    this.updateTransformOrigin({
      x: coords.current.x - this.containerRect.x,
      y: coords.current.y - this.containerRect.y,
    });

    const currentDistance = coords.pinch.distance.current;
    const previousDistance = coords.pinch.distance.previous;
    let difference = currentDistance / previousDistance;

    // console.table({ currentDistance, previousDistance, difference });

    if (this.scale === 0 && difference > 1) {
      // this is not great :\
      this.scale = 0.2;
      difference = 1;
    }

    this.adjustScaleByPercent(difference);

    this.render();
  };

  onDoubleClick = (event: Event, coords: GestureHelperData) => {
    event.preventDefault();

    const center = {
      x: coords.current.x - this.containerRect.x,
      y: coords.current.y - this.containerRect.y,
    };

    if ((event as KeyboardEvent).shiftKey) {
      this.zoomOut(center);
    } else {
      this.zoomIn(center);
    }
  };

  getScaledDimensions(scale: number) {
    return {
      width: this.contentRect.width * scale,
      height: this.contentRect.height * scale,
    };
  }

  render = (notify = true) => {
    const { contentEl } = this;

    if (!contentEl) {
      return;
    }

    const restrainedCoords = this.getRestrainedCoordinates(this.contentRect);

    this.contentRect.x = restrainedCoords.x;
    this.contentRect.y = restrainedCoords.y;

    // NO HEAVY LIFTING IN HERE!
    window.requestAnimationFrame(() => {
      if (this.originMarkerEl) {
        this.originMarkerEl.style.left = `${this.transformOrigin.x}px`;
        this.originMarkerEl.style.top = `${this.transformOrigin.y}px`;
      }

      const { x, y } = this.contentRect;

      let transform = `translate(${x}px, ${y}px) scale(${this.scale})`;

      if (use3DTransform) {
        transform += ' translateZ(0)';
      }

      contentEl.style.transform = transform;

      this.updateZoomControl();

      if (notify) {
        this.notifyUpdate();
      }
    });
  };

  animateToScale(value = 1, centerPoint?: Point, duration = 0.3, ease = 'quadOut') {
    const scale = clamp(value, this.minScale, 1);

    if (scale === this.scale) {
      return;
    }

    this.updateTransformOrigin(centerPoint);

    const startingScale = this.scale;
    const endingScale = scale;

    const tweeningData = {
      scale: startingScale,
    };

    this.startTween(tweeningData, { scale: endingScale, ease, duration }, () => {
      let changeInPercent;
      if (this.scale === 0) {
        this.scale = tweeningData.scale;
        changeInPercent = 1;
      } else {
        changeInPercent = tweeningData.scale / this.scale;
      }

      this.adjustScaleByPercent(changeInPercent);
      this.render();
    });
  }

  adjustScaleByPercent(adjustment: number) {
    this.scale = Math.max(0, this.scale * adjustment);

    const { contentRect, transformOrigin } = this;

    const dimensions = this.getScaledDimensions(this.scale);
    const previousDimensions = this.getScaledDimensions(this.previousScale);

    const originPercentX = (transformOrigin.x - contentRect.x) / previousDimensions.width;
    const originPercentY = (transformOrigin.y - contentRect.y) / previousDimensions.height;

    const zoomAdjustmentX = (previousDimensions.width - dimensions.width) * originPercentX;
    const zoomAdjustmentY = (previousDimensions.height - dimensions.height) * originPercentY;

    contentRect.x += zoomAdjustmentX;
    contentRect.y += zoomAdjustmentY;

    this.previousScale = this.scale;
  }

  onTweenComplete = () => {
    this.tween = undefined;
    this.contentRect.x = Math.round(this.contentRect.x);
    this.contentRect.y = Math.round(this.contentRect.y);

    this.render();
  };

  zoomIn = (center?: Point) => {
    let scale = this.scale * zoomMultiplier;

    if (scale >= 1 - zoomMinMaxThreshold) {
      scale = 1;
    }

    this.animateToScale(scale, center);
  };

  zoomOut = (center?: Point) => {
    let scale = this.scale / zoomMultiplier;

    if (scale <= this.minScale + zoomMinMaxThreshold) {
      scale = this.minScale;
    }

    this.animateToScale(scale, center);
  };

  fitContents = (animate = true) => {
    const { scale, minScale } = this.calcInitialScale();

    this.minScale = minScale;

    const { containerRect, contentRect } = this;
    const scaledDimensions = this.getScaledDimensions(scale);

    const x = Math.round((containerRect.width - scaledDimensions.width) * 0.5);
    const y = Math.round((containerRect.height - scaledDimensions.height) * 0.5);

    this.zoomControl.setCenteredZoomSettings({
      x,
      y,
      scale: makeZoomControlPerc(scale, minScale),
    });

    if (animate) {
      const tweeningData = {
        x: contentRect.x,
        y: contentRect.y,
        scale: this.scale,
      };

      this.startTween(tweeningData, {
        x, y, scale, ease: 'cubicInOut', duration: 0.33,
      }, () => {
        this.previousScale = this.scale;
        this.scale = tweeningData.scale;
        this.contentRect.x = tweeningData.x;
        this.contentRect.y = tweeningData.y;

        this.render();
      });
    } else {
      this.previousScale = scale;
      this.scale = scale;
      this.contentRect.x = x;
      this.contentRect.y = y;

      this.render();
    }
  };

  startTween(from: any, to: any, onUpdate: () => void) {
    this.tween?.cancel();

    this.tween = this.tweenr.to(from, to)
      .on('update', onUpdate)
      .on('complete', this.onTweenComplete)
      .on('cancelling', this.onTweenComplete);
  }

  destroy() {
    this.el.parentNode?.removeChild(this.el);

    this.tweenr.dispose();

    this.contextMenuBlocker.destroy();

    this.tween?.cancel();
    this.tween = undefined;

    this.notifyUpdate = noop;

    this.gestures.destroy();

    this.resizeHandlers.destroy();

    this.focusHandlers.destroy();

    this.keyDownHandler.destroy();
  }

  updateTransformOrigin(centerPoint?: Point) {
    if (centerPoint) {
      this.transformOrigin.x = centerPoint.x;
      this.transformOrigin.y = centerPoint.y;
    } else {
      this.transformOrigin.x = this.containerRect.half.width;
      this.transformOrigin.y = this.containerRect.half.height;
    }
  }

  updateZoomControl() {
    const { x, y } = this.contentRect;

    this.zoomControl.setCurrentZoomSettings({
      x,
      y,
      scale: makeZoomControlPerc(this.scale, this.minScale),
    });
  }
}

export default Viewport;
